import mapboxgl, { Map as MapboxMap, Popup } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useEffect, useRef, useState } from 'react';

import forestPng from '@/assets/svg/tree.png';
import forestsPng from '@/assets/svg/trees.png';

const TREES_SOURCE_ID = 'tree-click-test-trees';
const TREES_LAYER_ID = 'tree-click-test-trees-circles';
const TREE_CLUSTERS_LAYER_ID = 'tree-clusters';
const POPUP_STYLES_ID = 'tree-click-test-mapbox-popup-styles';
const DEFAULT_WFS_URL = `${import.meta.env.VITE_GEO_SERVER}/wfs?service=WFS&version=1.0.0&request=GetFeature&typeName=geonode:all_trees_combined&outputFormat=application/json&maxFeatures=500`;

const MAPBOX_POPUP_INJECTED_CSS = `
.mapboxgl-popup-content {
  padding: 0 !important;
  border-radius: 16px !important;
  box-shadow: 0 8px 24px rgba(0,0,0,0.15) !important;
  overflow: hidden !important;
  width: 200px !important;
  backdrop-filter: blur(4px) !important;
}

.mapboxgl-popup-tip {
  border-top-color: white !important;
}

.mapboxgl-popup {
  filter: drop-shadow(0 4px 12px rgba(0,0,0,0.15));
}
`;

function stripCrs(data: unknown): GeoJSON.GeoJSON {
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid GeoJSON');
  }
  const obj = { ...(data as Record<string, unknown>) };
  delete obj.crs;
  return obj as unknown as GeoJSON.GeoJSON;
}

function formatPlantingDate(value: unknown): string {
  if (typeof value !== 'string' || !value.trim()) {
    return '';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function hasPhotoUrl(feature: GeoJSON.Feature): boolean {
  const rawPhoto = feature.properties?.photo;
  const rawImage = feature.properties?.image;
  const isHttpUrl = (value: unknown) =>
    typeof value === 'string' && value.trim().startsWith('http');
  return isHttpUrl(rawPhoto) || isHttpUrl(rawImage);
}

interface TreeClickTestProps {
  wfsUrl?: string;
}

export const TreeClickTest = ({ wfsUrl = DEFAULT_WFS_URL }: TreeClickTestProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (document.getElementById(POPUP_STYLES_ID)) {
      return;
    }
    const styleEl = document.createElement('style');
    styleEl.id = POPUP_STYLES_ID;
    styleEl.textContent = MAPBOX_POPUP_INJECTED_CSS;
    document.head.appendChild(styleEl);
    return () => {
      document.getElementById(POPUP_STYLES_ID)?.remove();
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    let cancelled = false;
    let onTreeClustersClick: ((e: mapboxgl.MapLayerMouseEvent) => void) | undefined;
    let onTreeClustersMouseEnter: (() => void) | undefined;
    let onTreeClustersMouseLeave: (() => void) | undefined;

    mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;

    const map = new MapboxMap({
      container,
      style: import.meta.env.VITE_MAP_STYLE,
      center: [38.5, 7.5],
      zoom: 6,
    });
    const popup = new Popup({
      closeButton: false,
      closeOnClick: false,
      offset: 15,
      anchor: 'bottom',
      maxWidth: '200px',
    });

    const onMouseEnter = (e: mapboxgl.MapLayerMouseEvent) => {
      map.getCanvas().style.cursor = 'pointer';
      const features = map.queryRenderedFeatures(e.point, { layers: [TREES_LAYER_ID] });
      if (!features.length) {
        popup.remove();
        return;
      }
      const hoveredFeature = features[0];
      const props = hoveredFeature.properties ?? {};
      const geometry = hoveredFeature.geometry;
      if (!geometry || geometry.type !== 'Point') {
        popup.remove();
        return;
      }

      const coordinates = [...geometry.coordinates] as [number, number];
      const fid = props.fid != null ? String(props.fid) : '—';
      const photo = props.photo != null ? String(props.photo) : props.image != null ? String(props.image) : '';
      const location = props.location != null ? String(props.location) : 'Unknown location';
      const plantingDate = formatPlantingDate(props.planting_date);
      const plantingDateRow = plantingDate
        ? `<div style="margin-top:8px;">Planted: ${escapeHtml(plantingDate)}</div>`
        : '';

      const cardHTML = `
        <div style="padding:12px;background:white;font-size:14px;">
          <img src="${escapeHtml(photo)}" alt="Tree ${escapeHtml(fid)}" style="width:100%;height:140px;object-fit:cover;border-radius:12px 12px 0 0;display:block;" />
          <div style="margin-top:12px;"><strong>Tree #${escapeHtml(fid)}</strong></div>
          <div style="margin-top:8px;">📍 ${escapeHtml(location)}</div>
          ${plantingDateRow}
        </div>
      `;

      popup.setLngLat(coordinates).setHTML(cardHTML).addTo(map);
    };

    const onMouseLeave = () => {
      map.getCanvas().style.cursor = '';
      popup.remove();
    };

    map.on('mouseenter', TREES_LAYER_ID, onMouseEnter);
    map.on('mouseleave', TREES_LAYER_ID, onMouseLeave);

    map.once('load', async () => {
      console.log('map loaded, fetching:', wfsUrl);
      map.on('zoom', () => {
        const el = document.getElementById('zoom-indicator');
        if (el) el.textContent = 'Zoom: ' + map.getZoom().toFixed(2);
      });

      try {
        const res = await fetch(wfsUrl, { headers: { Accept: 'application/json' } });
        if (!res.ok) {
          throw new Error(`WFS ${res.status}`);
        }
        const raw = await res.json();
        if (cancelled) {
          return;
        }
        const geojson = stripCrs(raw) as GeoJSON.FeatureCollection;
        const features = Array.isArray(geojson.features) ? geojson.features : [];
        const filteredGeojson: GeoJSON.FeatureCollection = {
          ...geojson,
          features: features.filter(hasPhotoUrl),
        };
        const normalizedFeatures = filteredGeojson.features.map((feature) => {
          if (feature.geometry?.type === 'MultiPoint') {
            return {
              ...feature,
              geometry: {
                type: 'Point' as const,
                coordinates: feature.geometry.coordinates[0],
              },
            };
          }
          return feature;
        });
        const normalizedGeojson: GeoJSON.FeatureCollection = {
          ...filteredGeojson,
          features: normalizedFeatures,
        };
        const pointFeatures = filteredGeojson.features.filter(
          (feature): feature is GeoJSON.Feature<GeoJSON.Point | GeoJSON.MultiPoint> =>
            (feature.geometry?.type === 'Point' && Array.isArray(feature.geometry.coordinates)) ||
            (feature.geometry?.type === 'MultiPoint' &&
              Array.isArray(feature.geometry.coordinates) &&
              feature.geometry.coordinates.length > 0),
        );

        if (cancelled) {
          return;
        }

        await Promise.all([
          new Promise<void>((resolve, reject) => {
            map.loadImage(forestPng, (error, image) => {
              if (error || !image) {
                reject(error ?? new Error('Failed to load forests-marker'));
                return;
              }
              if (!map.hasImage('forests-marker')) {
                map.addImage('forests-marker', image);
              }
              resolve();
            });
          }),
          new Promise<void>((resolve, reject) => {
            map.loadImage(forestsPng, (error, image) => {
              if (error || !image) {
                reject(error ?? new Error('Failed to load forests-clusters'));
                return;
              }
              if (!map.hasImage('forests-clusters')) {
                map.addImage('forests-clusters', image);
              }
              resolve();
            });
          }),
        ]);

        if (cancelled) {
          return;
        }

        if (!map.getSource(TREES_SOURCE_ID)) {
          map.addSource(TREES_SOURCE_ID, {
            type: 'geojson',
            data: normalizedGeojson,
            cluster: true,
            clusterMaxZoom: 17,
            clusterRadius: 40,
          });
        }

        if (!map.getLayer(TREE_CLUSTERS_LAYER_ID)) {
          map.addLayer({
            id: TREE_CLUSTERS_LAYER_ID,
            type: 'symbol',
            source: TREES_SOURCE_ID,
            filter: ['has', 'point_count'],
            layout: {
              'icon-image': 'forests-clusters',
              'icon-size': 1.2,
              'icon-allow-overlap': true,
              'text-field': '{point_count_abbreviated}',
              'text-font': ['Oswald Regular', 'Arial Unicode MS Bold'],
              'text-size': 26,
              'text-anchor': 'center',
              'text-offset': [0, -0.1],
            },
            paint: { 'text-color': '#ffffff' },
          });
        }

        if (!map.getLayer(TREES_LAYER_ID)) {
          map.addLayer({
            id: TREES_LAYER_ID,
            type: 'symbol',
            source: TREES_SOURCE_ID,
            filter: ['!', ['has', 'point_count']],
            layout: {
              'icon-image': 'forests-marker',
              'icon-size': 1.2,
              'icon-allow-overlap': true,
            },
          });
        }

        onTreeClustersClick = (e: mapboxgl.MapLayerMouseEvent) => {
          const clusterFeatures = map.queryRenderedFeatures(e.point, {
            layers: [TREE_CLUSTERS_LAYER_ID],
          });
          const feature = clusterFeatures[0];
          if (!feature?.properties) {
            return;
          }
          const clusterId = feature.properties.cluster_id;
          if (typeof clusterId !== 'number') {
            return;
          }
          const source = map.getSource(TREES_SOURCE_ID) as mapboxgl.GeoJSONSource;
          source.getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err || zoom == null || cancelled) {
              return;
            }
            const geometry = feature.geometry;
            if (!geometry || geometry.type !== 'Point') {
              return;
            }
            map.easeTo({
              center: geometry.coordinates as [number, number],
              zoom: zoom + 1,
              duration: 600,
            });
          });
        };
        map.on('click', TREE_CLUSTERS_LAYER_ID, onTreeClustersClick);

        onTreeClustersMouseEnter = () => {
          map.getCanvas().style.cursor = 'pointer';
        };
        onTreeClustersMouseLeave = () => {
          map.getCanvas().style.cursor = '';
        };
        map.on('mouseenter', TREE_CLUSTERS_LAYER_ID, onTreeClustersMouseEnter);
        map.on('mouseleave', TREE_CLUSTERS_LAYER_ID, onTreeClustersMouseLeave);

        if (pointFeatures.length > 0) {
          let minLng = Number.POSITIVE_INFINITY;
          let minLat = Number.POSITIVE_INFINITY;
          let maxLng = Number.NEGATIVE_INFINITY;
          let maxLat = Number.NEGATIVE_INFINITY;

          for (const feature of pointFeatures) {
            const g = feature.geometry;
            const coordPair =
              g.type === 'Point' ? g.coordinates : g.coordinates[0];
            const [lng, lat] = coordPair;
            if (typeof lng !== 'number' || typeof lat !== 'number') {
              continue;
            }
            minLng = Math.min(minLng, lng);
            minLat = Math.min(minLat, lat);
            maxLng = Math.max(maxLng, lng);
            maxLat = Math.max(maxLat, lat);
          }

          if (
            Number.isFinite(minLng) &&
            Number.isFinite(minLat) &&
            Number.isFinite(maxLng) &&
            Number.isFinite(maxLat)
          ) {
            map.fitBounds(
              [[minLng, minLat], [maxLng, maxLat]],
              { padding: 60, duration: 1500 },
            );
          }
        }

        setLoadError(null);
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : 'Failed to load trees');
        }
      }
    });

    return () => {
      cancelled = true;
      if (onTreeClustersClick) {
        map.off('click', TREE_CLUSTERS_LAYER_ID, onTreeClustersClick);
      }
      if (onTreeClustersMouseEnter) {
        map.off('mouseenter', TREE_CLUSTERS_LAYER_ID, onTreeClustersMouseEnter);
      }
      if (onTreeClustersMouseLeave) {
        map.off('mouseleave', TREE_CLUSTERS_LAYER_ID, onTreeClustersMouseLeave);
      }
      map.off('mouseenter', TREES_LAYER_ID, onMouseEnter);
      map.off('mouseleave', TREES_LAYER_ID, onMouseLeave);
      map.getCanvas().style.cursor = '';
      popup.remove();
      map.remove();
    };
  }, [wfsUrl]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100vh' }}>
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      <div
        id="zoom-indicator"
        style={{
          position: 'absolute',
          bottom: 24,
          left: 12,
          background: 'rgba(0,0,0,0.6)',
          color: 'white',
          padding: '4px 8px',
          borderRadius: 4,
          fontSize: 12,
          zIndex: 10,
          pointerEvents: 'none',
        }}
      >
        Zoom: -
      </div>
      {loadError && (
        <div
          style={{
            position: 'absolute',
            top: 12,
            left: 12,
            right: 12,
            padding: 8,
            background: 'rgba(0,0,0,0.75)',
            color: '#ffb4b4',
            fontSize: 14,
            zIndex: 2,
            pointerEvents: 'none',
          }}
        >
          {loadError}
        </div>
      )}
    </div>
  );
};
